import { IconButton, InputAdornment } from '@mui/material';
import { Visibility, VisibilityOff } from '@material-ui/icons';

const errorMessages = {
  MANDATORY_FIELD: 'Campo obrigatório',
};

/**
 * Checks if all fields in the form are valid by using the provided validation function.
 *
 * @param {Object} form - The current form data.
 * @param {Function} validateField - Function that validates a single field.
 * @returns {boolean} True if all fields are valid (i.e., no error messages), false otherwise.
 */
const isStepValid = (form, validateField) => {
  const errors = Object.keys(form).map((field) => ({
    [field]: validateField(field, form[field]),
  }));
  const allErrorsEmpty = errors.every((error) => Object.values(error)[0] === '');
  return allErrorsEmpty;
};

/**
 * Validates all fields in the form and updates the form errors state.
 *
 * @param {Object} form - The current form data.
 * @param {Function} validateField - Function that validates a single field.
 * @param {Function} setFormErrors - Function to update the form error state.
 */
function validateAllFields(form, validateField, setFormErrors) {
  Object.keys(form).forEach((field) => {
    setFormErrors((prev) => ({ ...prev, [field]: validateField(field, form[field]) }));
  });
}

/**
 * Factory function that returns an async submit handler for form steps.
 *
 * @param {Object} form - The current form data.
 * @param {Function} validateField - Function that validates a single field.
 * @param {Function} setFormErrors - Function to update the form error state.
 * @param {boolean} isLoading - Indicates whether the form is currently submitting.
 * @param {Function} setIsLoading - Function to toggle loading state.
 * @param {Function} onSuccess - Function to be executed when the form is valid.
 * @param {Function} updateRedux - Function to dispatch form data to Redux.
 * @returns {Function} An async function to be used as a form submit handler.
 */
const handleSubmitFactory = (form, validateField, setFormErrors, isLoading, setIsLoading, onSuccess, updateRedux) => (
  async (e) => {
    e.preventDefault();
    setIsLoading(true);

    if (!isStepValid(form, validateField) || isLoading) {
      validateAllFields(form, validateField, setFormErrors);
      setIsLoading(false);
      return;
    }

    await onSuccess();
    updateRedux();
    setIsLoading(false);
  }
);

/**
 * Returns configuration for password input visibility toggle.
 *
 * @param {boolean} showPassword - Indicates whether the password is currently visible.
 * @param {Function} setShowPassword - Function to toggle password visibility.
 * @returns {Object} An object containing the InputAdornment with a visibility toggle button.
 */
const passwordIconFactory = (showPassword, setShowPassword) => ({
  endAdornment: (
    <InputAdornment position="start">
      <IconButton
        aria-label="toggle password visibility"
        edge="end"
        size="large"
        onClick={() => setShowPassword((prev) => !prev)}
        onMouseDown={(e) => e.preventDefault()}
      >
        {showPassword ? <Visibility /> : <VisibilityOff />}
      </IconButton>
    </InputAdornment>
  ),
});

export {
  errorMessages,
  isStepValid,
  handleSubmitFactory,
  passwordIconFactory,
};
